/**
 * What the reviewed-merge API said, in words a person can act on
 * (SPEC-invoice-merge-history-2026-10-02 R4 AC3, AC6, AC7).
 *
 * The SERVER decides every refusal; this module only reads its structured answer
 * (`{error: {code, message, field?, ...}}`, see backend services/invoice_merge_review.py
 * `MergeReviewError.detail`) and attaches the resolution path a person should take. There is
 * no eligibility rule here -- nothing decides whether two invoices may merge.
 *
 * Free of react and api imports so it is unit-testable alone.
 */
import type { MergePlanBlocking } from './invoice-merge-plan';
import { parseBlockingReservations, type BlockingReservation } from './invoice-payment-reconciliation';

export type MergeErrorKind =
  | 'review_required' // an old client called the legacy merge route
  | 'stale' // the plan changed after it was reviewed: review again, never retry with new content
  | 'invalid_choice' // a choice no longer fits the invoices
  | 'blocked' // a guarded conflict (D1 draw, cap conflict, ...)
  | 'choices_required'
  | 'acknowledgement_required'
  | 'future_default' // a future-default selection the server cannot honour
  | 'retryable' // nothing changed; try again
  | 'conflict' // e.g. the operation key was used for a different merge
  | 'permission'
  | 'not_found'
  | 'disabled'
  | 'unknown';

export interface MergeErrorInfo {
  kind: MergeErrorKind;
  status: number | null;
  code: string | null;
  message: string;
  /** The merge field the problem belongs to (a plan field key, or `future_defaults`). */
  field: string | null;
  /** What to do next, in one sentence. */
  resolution: string;
  retryable: boolean;
  /**
   * True only when the server answered with a 4xx: the request was refused and nothing was committed
   * under its operation key. A 5xx or a lost connection is NOT definitive; the outcome is unknown.
   */
  definitive: boolean;
  blocking: MergePlanBlocking[];
  missingChoices: string[];
  missingAcknowledgements: string[];
  changedInvoiceIds: string[];
  /** For a refused future default: WHICH choice (`payer`, `recipients`, ...) the server refused. */
  futureField: string | null;
  /** The checkout reservations behind `checkout_*` refusals (R5 iteration 4: staff can check them with the provider). */
  reservations: BlockingReservation[];
}

interface Detail {
  code?: string;
  message?: string;
  field?: string | null;
  retryable?: boolean;
  blocking?: unknown;
  missing_choices?: unknown;
  missing_acknowledgements?: unknown;
  changed_invoice_ids?: unknown;
  future_field?: unknown;
  reservations?: unknown;
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

/** The shape `fetchAPI` gives a failed call: an Error with the HTTP `status` and the parsed body in `data`. */
interface ApiFailureLike {
  status?: unknown;
  message?: unknown;
  data?: { error?: unknown; detail?: unknown };
}

function detailOf(failure: ApiFailureLike): Detail {
  const wrapped = failure?.data?.error;
  if (wrapped && typeof wrapped === 'object') return wrapped as Detail;
  const detail = failure?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) return detail as Detail;
  if (Array.isArray(detail) && detail.length > 0) {
    // FastAPI/pydantic validation (422): name the first offending field.
    const first = detail[0] as { loc?: unknown[]; msg?: string } | undefined;
    const loc = Array.isArray(first?.loc) ? first.loc.slice(1).join('.') : '';
    return { code: 'request_invalid', message: first?.msg ? `${loc ? `${loc}: ` : ''}${first.msg}` : undefined };
  }
  if (typeof wrapped === 'string') return { message: wrapped };
  if (typeof detail === 'string') return { message: detail };
  return {};
}

const RESOLUTIONS: Record<string, { kind: MergeErrorKind; resolution: string; retryable?: boolean }> = {
  merge_review_required: {
    kind: 'review_required',
    resolution: 'Use Review merge: it shows what changes, what happens to payments, and the combined invoice before anything is combined.',
  },
  reviewed_merge_disabled: { kind: 'disabled', resolution: 'Merging is not enabled for this account yet. Nothing was changed.' },
  stale_plan: { kind: 'stale', resolution: 'An invoice changed after you reviewed it. Review again; nothing was changed.' },
  plan_digest_mismatch: { kind: 'stale', resolution: 'The invoices or your choices no longer match what you reviewed. Review again; nothing was changed.' },
  plan_version_unsupported: { kind: 'stale', resolution: 'Reload this page and review again; nothing was changed.' },
  source_versions_incomplete: { kind: 'stale', resolution: 'Reload this page and review again; nothing was changed.' },
  invalid_plan_digest: { kind: 'stale', resolution: 'Review again; nothing was changed.' },
  merge_topology_changed: { kind: 'stale', resolution: 'The invoices were merged or changed while this waited. Review again.', retryable: true },
  merge_blocked: { kind: 'blocked', resolution: 'Resolve the problem listed below, then review again. Nothing was changed.' },
  merge_choices_required: { kind: 'choices_required', resolution: 'Choose a value for every field marked below.' },
  merge_acknowledgement_required: { kind: 'acknowledgement_required', resolution: 'Tick every required acknowledgement below.' },
  operation_key_conflict: {
    kind: 'conflict',
    resolution: 'This request was already used for a different merge. Review again and combine with a new request.',
  },
  checkout_in_progress: { kind: 'retryable', resolution: 'A client is paying one of these invoices. Wait a minute and try again; nothing was changed.', retryable: true },
  checkout_reconciliation_required: {
    kind: 'retryable',
    resolution: 'Press "Check with payment provider" below, then review again; nothing was changed.',
    retryable: true,
  },
  active_send_in_progress: { kind: 'retryable', resolution: 'A send is in progress or unconfirmed. Wait or resolve it from the invoice, then review again.', retryable: true },
  apply_retryable_lock_conflict: { kind: 'retryable', resolution: 'Another change to these invoices is in progress. Nothing was changed; try again in a moment.', retryable: true },
  unknown_choice_field: { kind: 'invalid_choice', resolution: 'Reload this page and review again.' },
  invalid_choice_source: { kind: 'invalid_choice', resolution: 'That choice does not apply to these invoices. Choose again.' },
  choice_malformed: { kind: 'invalid_choice', resolution: 'Choose again.' },
  choice_value_required: { kind: 'invalid_choice', resolution: 'Enter the value you want to use, or pick one of the originals.' },
  choice_value_invalid: { kind: 'invalid_choice', resolution: 'Correct the value, or pick one of the originals.' },
  due_date_before_invoice_date: { kind: 'invalid_choice', resolution: 'Pick a due date on or after the invoice date.' },
  unknown_acknowledgement: { kind: 'invalid_choice', resolution: 'Reload this page and review again.' },
  future_defaults_malformed: { kind: 'future_default', resolution: 'Choose "This pair only", or pick what should become the default.' },
  future_defaults_project_mismatch: {
    kind: 'future_default',
    resolution: 'A future default can only name the project these invoices belong to. Choose "This pair only".',
  },
  future_default_unsupported: {
    kind: 'future_default',
    resolution:
      'Only the billed client, recipients, DBA, billing address and hide-DBA setting can become project defaults. Everything else applies to this invoice only.',
  },
  future_default_payer_needs_scope: {
    kind: 'future_default',
    resolution: 'This billed client is not on the project yet. Add it to the project first, or choose "This pair only".',
  },
  future_default_billing_address_blank: {
    kind: 'future_default',
    resolution: 'Choose an address for the combined invoice first, or keep the address as this-invoice-only.',
  },
  future_default_recipient_needs_contact: {
    kind: 'future_default',
    resolution: 'Typed-in recipients cannot become a project default. Pick saved contacts, or choose "This pair only".',
  },
};

export function interpretMergeError(thrown: unknown): MergeErrorInfo {
  const error = (thrown ?? {}) as ApiFailureLike;
  const status: number | null = typeof error.status === 'number' ? error.status : null;
  const detail = detailOf(error);
  const code = typeof detail.code === 'string' ? detail.code : null;
  const known = code ? RESOLUTIONS[code] : undefined;

  let kind: MergeErrorKind = known?.kind ?? 'unknown';
  if (!known) {
    if (status === 404) kind = 'not_found';
    else if (status === 401 || status === 403) kind = 'permission';
  }

  const message =
    (typeof detail.message === 'string' && detail.message) ||
    (typeof error?.message === 'string' && error.message !== '[object Object]' && error.message) ||
    'The merge request failed.';

  let resolution = known?.resolution ?? '';
  if (!resolution) {
    if (kind === 'not_found') resolution = 'One of the invoices could not be found. Reload the page and choose the invoices again.';
    else if (kind === 'permission') resolution = 'You do not have permission to combine invoices. Ask someone who can delete invoices.';
    else if (status === null) resolution = 'We could not reach the server. Nothing is confirmed either way; check your connection.';
    else if (status >= 500) resolution = 'The server had a problem. We could not confirm whether anything changed.';
    else resolution = 'The request was refused; nothing was changed.';
  }

  return {
    kind,
    status,
    code,
    message,
    field: typeof detail.field === 'string' ? detail.field : null,
    resolution,
    retryable: known?.retryable === true || detail.retryable === true,
    definitive: status !== null && status >= 400 && status < 500,
    blocking: Array.isArray(detail.blocking) ? (detail.blocking as MergePlanBlocking[]) : [],
    missingChoices: stringList(detail.missing_choices),
    missingAcknowledgements: stringList(detail.missing_acknowledgements),
    changedInvoiceIds: stringList(detail.changed_invoice_ids),
    futureField: typeof detail.future_field === 'string' ? detail.future_field : null,
    reservations: parseBlockingReservations(detail.reservations),
  };
}

const wordsOf = (text: string): Set<string> =>
  new Set(text.toLowerCase().replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter((w) => w.length > 3));

/**
 * A notice: the server's message followed by what to do. Any clause of the resolution that only repeats what the
 * message (or an earlier clause) already said is left out, so a notice never repeats its own sentence; a clause
 * that adds something ("Pick saved contacts...", "nothing was changed") is kept.
 */
export function composeMergeNotice(info: Pick<MergeErrorInfo, 'message' | 'resolution'>): string {
  const message = info.message.trim();
  const resolution = info.resolution.trim();
  if (!resolution) return message;
  if (!message) return resolution;
  const seen = wordsOf(message);
  const kept: string[] = [];
  for (const clause of resolution.split(/[.;!?]+\s*/).map((c) => c.trim()).filter(Boolean)) {
    const words = wordsOf(clause);
    let shared = 0;
    for (const word of words) if (seen.has(word)) shared += 1;
    if (words.size > 0 && shared / words.size >= 0.7) continue;
    kept.push(`${clause.charAt(0).toUpperCase()}${clause.slice(1)}.`);
    for (const word of words) seen.add(word);
  }
  const lead = /[.!?]$/.test(message) ? message : `${message}.`;
  return kept.length === 0 ? message : `${lead} ${kept.join(' ')}`;
}

export interface BlockingGuidance {
  /** One line naming what to do about this blocker. */
  resolution: string;
  /** Which invoice (by id) the person should open to resolve it, when the server named one. */
  openInvoiceId: string | null;
  /** Offer the existing cancel flow (a wrong-project invoice is recreated on the right project, then cancelled). */
  offerCancel: boolean;
}

/** Resolution path for each guarded conflict the server can report (backend invoice_merge_plan.py). */
export function blockingGuidance(blocker: MergePlanBlocking): BlockingGuidance {
  switch (blocker.code) {
    case 'draw_invoice_not_supported':
      return {
        resolution:
          'Technical progress-draw invoices keep their own draw workflow (Schedule of Values and draw certificate). Open the draw invoice and continue it there; nothing here can combine it.',
        openInvoiceId: blocker.invoice_id ?? null,
        offerCancel: false,
      };
    case 'different_project':
      return {
        resolution:
          'A merge cannot cross projects. If one invoice is on the wrong project, recreate it on the right project, then cancel the wrong one.',
        openInvoiceId: null,
        offerCancel: true,
      };
    case 'different_currency':
      return { resolution: 'Currencies cannot be converted. Keep these as two invoices.', openInvoiceId: null, offerCancel: false };
    case 'already_merged':
      return {
        resolution: 'Open the invoice it was merged into and review from there.',
        openInvoiceId: blocker.current_invoice_id ?? null,
        offerCancel: false,
      };
    case 'active_send_in_progress':
      return {
        resolution: 'Wait for the send to finish, or resolve its outcome from the invoice, then review again.',
        openInvoiceId: blocker.invoice_id ?? null,
        offerCancel: false,
      };
    case 'billable_cap_conflict': {
      const other = blocker.conflicts?.find((c) => c.other_invoice_id)?.other_invoice_id ?? null;
      return {
        resolution:
          'Reviving this cancelled invoice would bill an item twice or past its cap. Fix the duplicate billing with the existing item correction on the invoice that already bills it, then review again.',
        openInvoiceId: other ?? blocker.invoice_id ?? null,
        offerCancel: false,
      };
    }
    case 'cached_payment_not_in_ledger':
      return {
        resolution:
          'The invoice shows paid money its payment history does not contain. Open its payment history and reconcile the missing receipt first; no receipt is invented.',
        openInvoiceId: blocker.invoice_id ?? null,
        offerCancel: false,
      };
    case 'unsupported_status':
      return { resolution: 'Open the invoice and correct its status first.', openInvoiceId: blocker.invoice_id ?? null, offerCancel: false };
    case 'duplicate_line_identity':
    case 'duplicate_payment_identity':
      return { resolution: 'The same record appears on both invoices. Contact support before merging.', openInvoiceId: null, offerCancel: false };
    default:
      return { resolution: 'Resolve this, then review again. Nothing was changed.', openInvoiceId: blocker.invoice_id ?? null, offerCancel: false };
  }
}

/** Human labels for the six production lifecycle states (display only). */
export const MERGE_STATE_LABELS: Record<string, string> = {
  draft: 'Draft',
  ready: 'Ready',
  sent: 'Sent',
  partially_paid: 'Partially paid',
  paid: 'Paid',
  cancelled: 'Cancelled',
};

export function mergeStateLabel(status: string): string {
  return MERGE_STATE_LABELS[status] ?? status.replaceAll('_', ' ');
}

/** "$7,875.00" from the plan's canonical two-decimal string. Display only. */
export function formatMergeMoney(value: string): string {
  const [whole, cents = '00'] = value.split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return `$${grouped}.${cents}`;
}
